import { FC, useMemo } from "react";
import TableComponent, {
  TableProps,
} from "../../../../../common/components/table/table.component";
import { useAppContext } from "../../../../../context/app.context";
import OrderOptimizerRailyListingTableColumn from "../common/types/order-optimizer-raily-listing-table-column";
import orderOptimizerRailyListingTableHelper from "./order-optimizer-raily-listing-table.helpers";
import OrderOptimizerRailyListingTableRow from "../common/types/order-optimizer-raily-listing-table-row";
import OrderOptimizerRailyListingTableRowComponent from "./row/order-optimizer-raily-listing-table-row.component";
import orderOptimizerRoutesHelper from "../../../common/routes/order-optimizer-routes.helper";
import OrderOptimizerOptimizationStatusParams from "../../../../../common/services/order-optimizer/optimization-status/order-optimizer-optimization-status-params";
import useAbort from "../../../../../common/hooks/use-abort";
import orderOptimizerService from "../../../../../common/services/order-optimizer/order-optimizer.service";
import OrderOptimizerOptimizationStatus from "../../../../../common/services/order-optimizer/optimization-status/order-optimizer-optimization-status";
import notificationService from "../../../../../common/utils/notification/notification.service";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import { OrderOptimizerRailyListingItem } from "../common/types/order-optimizer-raily-listing-item";

type OrderOptimizerRailyListingTableProps = Pick<
  TableProps,
  "isError" | "isLoading"
> & {
  listingItems: OrderOptimizerRailyListingItem[];
  selectedHumanId: number | undefined;
  applyOptimizationStatus: (
    optimizationStatus: OrderOptimizerOptimizationStatus,
    optimizationId: string,
  ) => void;
};

const OrderOptimizerRailyListingTableComponent: FC<
  OrderOptimizerRailyListingTableProps
> = (props) => {
  const { selectedAppLanguage, user } = useAppContext();
  const translation =
    orderOptimizerTranslationsHelper.getRailyListingTranslations().table;

  const columns = useMemo(
    (): OrderOptimizerRailyListingTableColumn[] =>
      orderOptimizerRailyListingTableHelper.getColumns(),
    [],
  );

  const orderOptimizerOptimizationStatusAbort = useAbort();

  const onOptimizationClick = async (listingItemUuid: string) => {
    const optimizationStatusLoadParams: OrderOptimizerOptimizationStatusParams =
      {
        optimizationId: listingItemUuid,
      };

    await orderOptimizerService
      .getOptimizationStatus(
        optimizationStatusLoadParams,
        orderOptimizerOptimizationStatusAbort.signal,
      )
      .then((optimizationStatus) => {
        if (
          !!optimizationStatus.reservedAt &&
          !!optimizationStatus.reservedBy
        ) {
          props.applyOptimizationStatus(optimizationStatus, listingItemUuid);
        } else {
          const currentUserStatus: OrderOptimizerOptimizationStatus = {
            reservedAt: new Date(),
            reservedBy: `${user?.firstName} ${user?.lastName}`,
          };

          props.applyOptimizationStatus(currentUserStatus, listingItemUuid);
        }
      })
      .then(() => {
        window.open(
          orderOptimizerRoutesHelper.getDetailsRoute({
            id: listingItemUuid,
          }),
          "_blank",
        );
      })
      .catch(() => {
        notificationService.error(
          translation.optimizationReservationErrorNotification,
        );
      });
  };

  const createTableRow = (
    listingItem: OrderOptimizerRailyListingItem,
  ): OrderOptimizerRailyListingTableRow => {
    return {
      id: listingItem.id,
      value: {
        row: <></>,
      },
      hiddenComponent: (
        <OrderOptimizerRailyListingTableRowComponent
          listingItem={listingItem}
          selectedHumanId={props.selectedHumanId}
          onOptimizationRequestClick={() => onOptimizationClick(listingItem.id)}
        />
      ),
      isHiddenComponentVisible: true,
    };
  };

  const rows: OrderOptimizerRailyListingTableRow[] = useMemo(() => {
    return props.listingItems.map((list) => createTableRow(list));
  }, [props.listingItems, selectedAppLanguage]);

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isLoading={props.isLoading}
      isError={props.isError}
      noHeader
    />
  );
};

export default OrderOptimizerRailyListingTableComponent;
